import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import {
  Lightning, Plus, Trash, Play, X, Clock, Eye, Door,
  Lightbulb, Fan, Lock, Siren, ShieldCheck, Power,
  ToggleLeft, ToggleRight, PencilSimple, Check
} from '@phosphor-icons/react';
import { SwipeableCard } from '@/components/SwipeableCard';

const TRIGGER_TYPES = [
  { id: 'time', label: 'Time', desc: 'At a specific time', icon: Clock },
  { id: 'motion', label: 'Motion', desc: 'Motion detected in room', icon: Eye },
  { id: 'sensor', label: 'Sensor', desc: 'Sensor state change', icon: Door },
  { id: 'manual', label: 'Manual', desc: 'Trigger manually only', icon: Play },
];

const ACTION_TYPES = [
  { id: 'ha_service', label: 'Smart Device', service: 'light.turn_on', icon: Lightbulb },
  { id: 'ha_service_off', label: 'Turn Off Device', service: 'light.turn_off', icon: Power },
  { id: 'ha_service_fan', label: 'Fan Control', service: 'fan.turn_on', icon: Fan },
  { id: 'ha_service_lock', label: 'Lock Door', service: 'lock.lock', icon: Lock },
  { id: 'ha_service_unlock', label: 'Unlock Door', service: 'lock.unlock', icon: Lock },
  { id: 'security_mode', label: 'Set Security Mode', icon: ShieldCheck },
  { id: 'notification', label: 'Send Notification', icon: Siren },
];

export default function RoomAutomationPage() {
  const { api } = useAuth();
  const [rules, setRules] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [triggering, setTriggering] = useState('');
  const [form, setForm] = useState({
    name: '', room: '', trigger_type: 'time', trigger_value: '', actions: [], enabled: true, description: ''
  });

  const fetchData = useCallback(async () => {
    try {
      const [rulesRes, roomsRes] = await Promise.all([
        api.get('/automation-rules/rules'),
        api.get('/control-center/rooms'),
      ]);
      setRules(rulesRes.data);
      setRooms(roomsRes.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [api]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSave = async () => {
    if (!form.name.trim() || !form.room) return;
    try {
      if (editingId) {
        await api.put(`/automation-rules/rules/${editingId}`, form);
      } else {
        await api.post('/automation-rules/rules', form);
      }
      setShowForm(false); setEditingId(null);
      setForm({ name: '', room: '', trigger_type: 'time', trigger_value: '', actions: [], enabled: true, description: '' });
      fetchData();
    } catch (e) { console.error(e); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this rule?')) return;
    try { await api.delete(`/automation-rules/rules/${id}`); fetchData(); } catch (e) { console.error(e); }
  };

  const handleToggle = async (id) => {
    try { await api.put(`/automation-rules/rules/${id}/toggle`); fetchData(); } catch (e) { console.error(e); }
  };

  const handleTrigger = async (id) => {
    setTriggering(id);
    try {
      const res = await api.post(`/automation-rules/rules/${id}/trigger`);
      alert(res.data.message || 'Rule triggered');
      fetchData();
    } catch (e) { alert('Failed: ' + (e.response?.data?.detail || e.message)); }
    setTriggering('');
  };

  const addAction = (actionType) => {
    const at = ACTION_TYPES.find(a => a.id === actionType);
    if (!at) return;
    setForm(f => ({ ...f, actions: [...f.actions, {
      type: at.id.startsWith('ha_service') ? 'ha_service' : at.id,
      service: at.service || '', target: '', label: at.label, params: {},
      ...(at.id === 'security_mode' ? { mode: 'night' } : {}),
    }]}));
  };

  const removeAction = (idx) => {
    setForm(f => ({ ...f, actions: f.actions.filter((_, i) => i !== idx) }));
  };

  if (loading) return (
    <div className="flex items-center justify-center py-32">
      <div className="w-8 h-8 border-2 border-[#FF6B35] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="room-automation-page">
      <div className="flex items-start sm:items-center justify-between gap-3">
        <div className="min-w-0">
          <h1 className="font-heading text-xl sm:text-2xl font-bold tracking-tight">Room Automation</h1>
          <p className="text-xs sm:text-sm text-muted-foreground mt-1">Create trigger-action rules for each room</p>
        </div>
        <button onClick={() => { setShowForm(true); setEditingId(null); setForm({ name: '', room: rooms[0]?.name || '', trigger_type: 'time', trigger_value: '', actions: [], enabled: true, description: '' }); }}
          data-testid="add-rule-btn"
          className="px-3 sm:px-4 py-2.5 rounded-2xl bg-[#FF6B35] text-white text-xs sm:text-sm font-semibold flex items-center gap-1.5 sm:gap-2 btn-glow flex-shrink-0">
          <Plus size={16} /> <span className="hidden sm:inline">New</span> Rule
        </button>
      </div>

      {/* Rules List */}
      {rules.length === 0 ? (
        <div className="text-center py-16 rounded-2xl" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}>
          <Lightning size={40} className="text-neutral-600 mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">No automation rules yet. Create your first rule!</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="rules-list">
          {rules.map(rule => {
            const TriggerIcon = TRIGGER_TYPES.find(t => t.id === rule.trigger_type)?.icon || Clock;
            return (
              <SwipeableCard
                key={rule.rule_id}
                onSwipeRight={() => handleTrigger(rule.rule_id)}
                onSwipeLeft={() => handleToggle(rule.rule_id)}
                rightLabel="Run"
                leftLabel={rule.enabled ? 'Disable' : 'Enable'}
                rightColor="rgba(34,197,94,0.15)"
                leftColor="rgba(245,158,11,0.15)"
              >
                <div className="rounded-2xl p-3 sm:p-4" data-testid={`rule-${rule.rule_id}`}
                  style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', opacity: rule.enabled ? 1 : 0.5 }}>
                  <div className="flex items-start sm:items-center justify-between gap-2">
                    <div className="flex items-start sm:items-center gap-2.5 sm:gap-3 min-w-0 flex-1">
                      <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(255,107,53,0.1)' }}>
                        <TriggerIcon size={18} className="text-[#FF6B35]" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold truncate">{rule.name}</p>
                        <div className="flex items-center gap-1.5 sm:gap-2 text-[10px] text-muted-foreground flex-wrap">
                          <span className="uppercase">{rule.room}</span>
                          <span>·</span>
                          <span>{rule.trigger_type}: {rule.trigger_value || 'manual'}</span>
                          <span>·</span>
                          <span>{rule.actions?.length || 0} actions</span>
                          {rule.trigger_count > 0 && <><span>·</span><span>Ran {rule.trigger_count}x</span></>}
                          {rule.ai_generated && <span className="text-[#FF6B35] font-bold ml-1">AI</span>}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
                      <button onClick={() => handleTrigger(rule.rule_id)} disabled={triggering === rule.rule_id}
                        data-testid={`trigger-rule-${rule.rule_id}`}
                        className="p-1.5 sm:p-2 rounded-xl hover:bg-white/5 text-muted-foreground hover:text-[#FF6B35]">
                        <Play size={14} weight="fill" />
                      </button>
                      <button onClick={() => handleToggle(rule.rule_id)}
                        className="p-1.5 sm:p-2 rounded-xl hover:bg-white/5">
                        {rule.enabled ? <ToggleRight size={20} weight="fill" className="text-green-400" /> : <ToggleLeft size={20} className="text-neutral-500" />}
                      </button>
                      <button onClick={() => { setEditingId(rule.rule_id); setForm({ name: rule.name, room: rule.room, trigger_type: rule.trigger_type, trigger_value: rule.trigger_value, actions: rule.actions || [], enabled: rule.enabled, description: rule.description || '' }); setShowForm(true); }}
                        className="p-1.5 sm:p-2 rounded-xl hover:bg-white/5 text-muted-foreground"><PencilSimple size={14} /></button>
                      <button onClick={() => handleDelete(rule.rule_id)}
                        className="p-1.5 sm:p-2 rounded-xl hover:bg-white/5 text-muted-foreground hover:text-red-400"><Trash size={14} /></button>
                    </div>
                  </div>
                  <p className="text-[9px] text-neutral-600 mt-1 md:hidden">Swipe: right to run, left to toggle</p>
                </div>
              </SwipeableCard>
            );
          })}
        </div>
      )}

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(8px)' }}>
          <div className="w-full max-w-lg mx-4 max-h-[85vh] overflow-y-auto rounded-3xl p-6" data-testid="rule-form-modal"
            style={{ background: 'rgba(30,30,30,0.98)', border: '1px solid rgba(255,255,255,0.1)' }}>
            <h3 className="text-lg font-bold mb-4">{editingId ? 'Edit Rule' : 'New Automation Rule'}</h3>
            <div className="space-y-4">
              <div>
                <label className="text-xs font-semibold text-muted-foreground uppercase block mb-1">Rule Name</label>
                <input type="text" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  data-testid="rule-name-input" placeholder="e.g., Night Lights On"
                  className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-sm outline-none focus:border-[#FF6B35]/50" />
              </div>
              <div>
                <label className="text-xs font-semibold text-muted-foreground uppercase block mb-1">Room</label>
                <select value={form.room} onChange={e => setForm(f => ({ ...f, room: e.target.value }))}
                  data-testid="rule-room-select"
                  className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-sm outline-none">
                  <option value="">Select room</option>
                  {rooms.map(r => <option key={r.room_id} value={r.name}>{r.name}</option>)}
                  <option value="All Rooms">All Rooms</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold text-muted-foreground uppercase block mb-1">Trigger</label>
                <div className="grid grid-cols-2 gap-2">
                  {TRIGGER_TYPES.map(t => {
                    const Icon = t.icon;
                    return (
                      <button key={t.id} onClick={() => setForm(f => ({ ...f, trigger_type: t.id }))}
                        className={`p-3 rounded-xl text-left transition-all ${form.trigger_type === t.id ? 'ring-2 ring-[#FF6B35]' : ''}`}
                        style={{ background: form.trigger_type === t.id ? 'rgba(255,107,53,0.1)' : 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
                        <Icon size={16} className="text-[#FF6B35] mb-1" />
                        <p className="text-xs font-semibold">{t.label}</p>
                        <p className="text-[9px] text-muted-foreground">{t.desc}</p>
                      </button>
                    );
                  })}
                </div>
              </div>
              {form.trigger_type === 'time' && (
                <div>
                  <label className="text-xs font-semibold text-muted-foreground uppercase block mb-1">Time</label>
                  <input type="time" value={form.trigger_value} onChange={e => setForm(f => ({ ...f, trigger_value: e.target.value }))}
                    className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-sm outline-none" />
                </div>
              )}
              {form.trigger_type === 'sensor' && (
                <div>
                  <label className="text-xs font-semibold text-muted-foreground uppercase block mb-1">Sensor Event</label>
                  <select value={form.trigger_value} onChange={e => setForm(f => ({ ...f, trigger_value: e.target.value }))}
                    className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-sm outline-none">
                    <option value="">Select event</option>
                    <option value="door_open">Door Opened</option>
                    <option value="door_close">Door Closed</option>
                    <option value="window_open">Window Opened</option>
                    <option value="smoke_detected">Smoke Detected</option>
                    <option value="water_leak">Water Leak</option>
                  </select>
                </div>
              )}
              {form.trigger_type === 'motion' && (
                <div>
                  <label className="text-xs font-semibold text-muted-foreground uppercase block mb-1">Motion Condition</label>
                  <select value={form.trigger_value} onChange={e => setForm(f => ({ ...f, trigger_value: e.target.value }))}
                    className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-sm outline-none">
                    <option value="motion_detected">Motion Detected</option>
                    <option value="no_motion_5min">No Motion for 5 min</option>
                    <option value="no_motion_15min">No Motion for 15 min</option>
                    <option value="no_motion_30min">No Motion for 30 min</option>
                  </select>
                </div>
              )}
              <div>
                <label className="text-xs font-semibold text-muted-foreground uppercase block mb-2">Actions</label>
                {form.actions.map((action, i) => (
                  <div key={i} className="flex items-center gap-2 mb-2 p-3 rounded-xl" style={{ background: 'rgba(255,255,255,0.03)' }}>
                    <Lightning size={14} className="text-[#FF6B35] flex-shrink-0" />
                    <span className="text-sm flex-1">{action.label || action.type}</span>
                    <button onClick={() => removeAction(i)} className="p-1 text-red-400"><X size={12} /></button>
                  </div>
                ))}
                <div className="flex gap-2 flex-wrap">
                  {ACTION_TYPES.map(at => (
                    <button key={at.id} onClick={() => addAction(at.id)}
                      className="px-3 py-1.5 rounded-lg text-[10px] font-semibold flex items-center gap-1"
                      style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }}>
                      <at.icon size={10} /> {at.label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold text-muted-foreground uppercase block mb-1">Description (optional)</label>
                <input type="text" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                  placeholder="What does this rule do?"
                  className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-sm outline-none" />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={() => { setShowForm(false); setEditingId(null); }}
                className="flex-1 py-3 rounded-xl text-sm font-semibold text-neutral-400" style={{ background: 'rgba(255,255,255,0.05)' }}>
                Cancel
              </button>
              <button onClick={handleSave} data-testid="save-rule-btn"
                className="flex-1 py-3 rounded-xl bg-[#FF6B35] text-white text-sm font-bold">
                {editingId ? 'Update Rule' : 'Create Rule'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
